import type {
  BackendExperiment,
  ExperimentApiParams,
} from '@core/abTesting/backend/types'
import { defaultApiHost } from '@src/helpers'

import { logABTestingInfo } from '../logging'

/**
 * Fetches experiments from the backend API
 */
export async function fetchExperiments(
  params: ExperimentApiParams,
): Promise<BackendExperiment[] | null> {
  try {
    const apiUrl = defaultApiHost

    const url = new URL(`${apiUrl}/embed/v2/experiments`)
    url.searchParams.set('business_id', params.business_id)
    url.searchParams.set('guest_id', params.guestId)
    if (params.channel_id) {
      url.searchParams.set('channel_id', params.channel_id)
    }

    logABTestingInfo(`Fetching experiments from: ${url.toString()}`)

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`,
      )
    }

    const data = await response.json()

    // Handle error response from backend
    if (data && typeof data === 'object' && 'error' in data) {
      if (data.error === 'Experiment not found for a business') {
        // Return [] (not null) so callers can cache the empty result and
        // avoid refetching on every page load. null is reserved for failures.
        logABTestingInfo(
          'No experiments found for this business - not participating in A/B tests',
        )
        return []
      }

      return null
    }

    // Handle successful response with experiments array
    const experiments: BackendExperiment[] = data || []
    logABTestingInfo(`Received ${experiments.length} experiments from backend`)

    return experiments
  } catch (error) {
    logABTestingInfo('Failed to fetch experiments from backend')

    // Return null on error to fail gracefully
    return null
  }
}
